import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useToastStore } from '../../stores/ToastStore';
import { Toast } from './Toast';

/** Z-index chosen to sit above modals and overlays from the app's stack */
const TOAST_Z_INDEX = 5000;

/**
 * Renders global toasts into a body-level portal when requested by the store.
 *
 * Why: Ensures global notifications remain visible above modals/overlays and
 * aren't clipped by container overflow. Also enables consistent bottom-center
 * placement regardless of app layout.
 * 
 * How: Creates a container on demand and routes only when `placement` is
 * 'bottom-center' or when `adaptToModal` is true and a modal is open.
 *
 * Usage:
 * - Place `<ToastPortal />` once near the root of your app.
 * - Call `showToast(..., { placement: 'bottom-center' })` to render here.
 *
 * @example App setup
 * ```tsx
 * <App />
 * <ToastPortal />
 * ```
 */
export function ToastPortal(): JSX.Element | null {
    const { toast, isModalOpen } = useToastStore();
    const isOpen = toast !== null;

    const portalRoot = document.body;
    const container = useMemo(() => document.createElement('div'), []);

    useEffect(() => {
        if (!isOpen) return undefined;
        portalRoot.appendChild(container);
        return () => {
            if (portalRoot.contains(container)) {
                portalRoot.removeChild(container);
            }
        };
    }, [container, portalRoot, isOpen]);

    if (!isOpen || !toast) return null;

    // Ensure above modals and keep underlying UI interactive except on buttons
    container.style.zIndex = TOAST_Z_INDEX.toString();
    container.style.pointerEvents = 'none';

    // Decide whether this toast should render in the portal
    const shouldRenderInPortal = () => {
        // Modal-adaptive toasts render in portal only while a modal is open
        if (toast.adaptToModal) {
            return isModalOpen;
        }
        // Fixed placement — route by explicit request
        return toast.placement === 'bottom-center';
    };

    if (!shouldRenderInPortal()) {
        return null; // Let Chat component handle this toast
    }

    container.style.position = 'fixed';
    container.style.left = '50%';
    container.style.transform = 'translateX(-50%)';
    container.style.bottom = '24px';
    container.style.transition = 'bottom 0.3s ease-out';

    return ReactDOM.createPortal(
        <Toast
            inPortal
            placement="bottom-center"
            isVisible
            message={toast.message}
            type={toast.type}
            progress={toast.progress}
        />,
        container,
    );
}